/**
 * VPDPage — `/pulse/vpd`.
 *
 * Detail screen for Vapor Pressure Deficit. Shows live VPD (kPa), the
 * 48h trend (with 0.8-1.2 ideal band), an impact insight block, and a
 * recommendation tied to the current band (no transpiration vs ready to spray).
 */
import React, { useEffect, useState } from 'react';
import { Wind, AlertTriangle, Lightbulb, Brain } from 'lucide-react';
import { PulseDetailLayout, PulseTile, PulseInsight } from '../../components/pulse/PulseDetailLayout';
import { PulseSparkline } from '../../components/pulse/PulseSparkline';
import { API_URL } from '../../config/api';

function statusFor(v) {
  if (v == null) return { text: '—', color: 'text-slate-400' };
  if (v < 0.4) return { text: 'Very Low', color: 'text-rose-400' };
  if (v < 0.8) return { text: 'Low',      color: 'text-amber-400' };
  if (v < 1.2) return { text: 'Ideal',    color: 'text-emerald-400' };
  if (v < 1.5) return { text: 'Marginal', color: 'text-amber-400' };
  if (v < 2.0) return { text: 'Poor',     color: 'text-amber-500' };
  return { text: 'Extreme', color: 'text-rose-500' };
}

export default function VPDPage() {
  const [now, setNow] = useState(null);
  const [hist, setHist] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/dashboard/now`, { credentials: 'include' })
      .then(r => r.json()).then(setNow).catch(() => {});
    fetch(`${API_URL}/api/dashboard/history?metric=vpd&hours=48`, { credentials: 'include' })
      .then(r => r.json()).then(setHist).catch(() => {});
  }, []);

  const value = now?.metrics?.vpd?.value;
  const st = statusFor(value);
  const trend = hist?.trend;
  const delta = hist?.delta;

  return (
    <PulseDetailLayout
      title="VPD"
      icon={Wind}
      iconColor="text-purple-400"
      bigValue={value != null ? value.toFixed(2) : null}
      bigValueColor="text-purple-400"
      badgeText={st.text}
      badgeColor={st.color}
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold pl-1">
        Vapor Pressure Deficit · 48h · kPa
      </div>
      <PulseSparkline metric="vpd" hours={48} idealMin={0.8} idealMax={1.2} unit=" kPa" />

      <div className="grid grid-cols-2 gap-3">
        <PulseTile
          testid="vpd-ideal-range-tile"
          label="Ideal Range"
          value="0.8 – 1.2"
          valueColor="text-emerald-400"
          subline="kPa"
        />
        <PulseTile
          testid="vpd-trend-tile"
          label="Trend"
          value={trend === 'rising' ? '↑ Rising'
                : trend === 'falling' ? '↓ Falling'
                : '· Flat'}
          valueColor={trend === 'rising' ? 'text-emerald-400'
                      : trend === 'falling' ? 'text-amber-400'
                      : 'text-slate-100'}
          subline={delta != null ? `${delta > 0 ? '+' : ''}${delta.toFixed(2)} kPa / 48h` : '—'}
        />
      </div>

      <PulseInsight tone="purple" icon={Brain} label="Insight" testid="vpd-insight">
        {value == null
          ? 'Add a humidity sensor to compute VPD. Without it, plant activity is a guess.'
          : value < 0.4
            ? 'Air is saturated. No transpiration is occurring. Plant is not actively growing.'
            : value < 0.8
              ? 'Low transpiration pull — plant uptake is sluggish.'
              : value < 1.2
                ? 'Optimal transpiration. Nutrient uptake is firing on all cylinders.'
                : 'High transpiration — plant pulling hard. Stress risk if it climbs further.'}
      </PulseInsight>

      {value != null && value < 0.4 && (
        <PulseInsight tone="rose" icon={AlertTriangle} label="Impact" testid="vpd-impact">
          • No nutrient uptake<br/>
          • Sprays will not be effective<br/>
          • Growth is paused
        </PulseInsight>
      )}

      <PulseInsight tone="emerald" icon={Lightbulb} label="Recommendation" testid="vpd-recommendation">
        {value == null
          ? 'Waiting on humidity sensor reading.'
          : value < 0.4
            ? 'Wait for VPD to rise into the ideal range — applying anything now is wasted.'
            : value < 0.8
              ? 'Hold off on foliar inputs — uptake is sub-optimal. Granular OK.'
              : value < 1.2
                ? 'Apply foliar sprays now — uptake window is open.'
                : 'Stop spraying. Push moisture instead — plant is fighting heat stress.'}
      </PulseInsight>
    </PulseDetailLayout>
  );
}
